import type { SpatialProjection } from "../../domain/mesh";
import {
  BLACK_HOLE_DISK_PLANE_X as BH_PLANE_X,
  BLACK_HOLE_DISK_PLANE_Z as BH_PLANE_Z,
} from "../../engine/config/blackHolePlane";

const edge = (bend: number, skew: number, lift: number, opacityScale: number, thickness = 0.0065) => ({
  bend,
  skew,
  lift,
  thickness,
  opacityScale,
});
const orbit = (
  radius: number,
  speed: number,
  tiltX: number,
  tiltZ: number,
  phase: number,
  tone: "core" | "infra" | "memory" | "compute" | "signal",
  scale = 0.066,
  glow = 0.68,
  ringOpacity = 0.13,
) => ({
  orbit: { radius, speed, tiltX, tiltZ, eccentricity: 0.035, phase, ringOpacity },
  visual: { scale, ringCount: radius < 1.9 ? 1 : 0, glow, tone },
});

export const mockProjection: SpatialProjection = {
  nodes: {
    "kaim-dev": {
      position: [0, 0, 0],
      magnitude: 1.15,
      archetype: "black-hole",
      cartography: { labelTier: "hero", labelPlacement: "south", labelOffsetY: 76 },
    },
    goals: {
      position: [-8.95, 3.58, -0.94],
      magnitude: 1.01,
      archetype: "stellar",
      cartography: { labelTier: "major", labelPlacement: "south", labelOffsetY: 22 },
    },
    build: {
      position: [9.2, 3.46, -0.58],
      magnitude: 1.08,
      archetype: "stellar",
      cartography: { labelTier: "major", labelPlacement: "south", labelOffsetY: 22 },
    },
    photos: {
      position: [-7.64, -6.48, -1.34],
      magnitude: 0.92,
      archetype: "stellar",
      cartography: { labelTier: "major", labelPlacement: "south", labelOffsetY: 20 },
    },
    breakglass: {
      position: [7.7, -6.1, -1.42],
      magnitude: 0.82,
      archetype: "stellar",
      cartography: { labelTier: "major", labelPlacement: "south", labelOffsetY: 20 },
    },
    home: {
      position: [0.12, -8.96, -1.1],
      magnitude: 0.84,
      archetype: "stellar",
      cartography: { labelTier: "major", labelPlacement: "south", labelOffsetY: 20 },
    },
    okolica: {
      position: [11.52, -1.44, -1.72],
      magnitude: 0.88,
      archetype: "stellar",
      cartography: { labelTier: "major", labelPlacement: "west", labelOffsetX: 22 },
    },
    michalowice: {
      position: [12.05, 7.05, -2.0],
      magnitude: 0.54,
      archetype: "stellar",
      cartography: { labelTier: "minor", labelPlacement: "west", labelOffsetX: 16 },
    },
    "house-goal": {
      position: [3.95, 7.68, -1.72],
      magnitude: 0.54,
      archetype: "stellar",
      cartography: { labelTier: "minor", labelPlacement: "south-west", labelOffsetY: 10 },
    },
    house: {
      position: [8.15, 7.55, -1.88],
      magnitude: 0.62,
      archetype: "stellar",
      cartography: { labelTier: "minor", labelPlacement: "north", labelOffsetY: 12 },
    },
    "market-engine": {
      position: [-11.62, 0.98, -1.98],
      magnitude: 0.48,
      archetype: "stellar",
      cartography: { labelTier: "minor", labelPlacement: "east", labelOffsetX: 14 },
    },
    "portfolio-frontier": {
      position: [1.96, 4.92, -1.26],
      magnitude: 0.44,
      archetype: "stellar",
      cartography: { labelTier: "minor", labelPlacement: "south", labelOffsetY: 10 },
    },
    "photo-archive": {
      position: [-9.84, -7.82, -2.18],
      magnitude: 0.4,
      archetype: "stellar",
      cartography: { labelTier: "minor", labelPlacement: "east", labelOffsetX: 12 },
    },
    "afery-lekarskie": {
      position: [-12.2, -3.5, -1.82],
      magnitude: 0.68,
      archetype: "stellar",
      cartography: { labelTier: "major", labelPlacement: "east", labelOffsetX: 18 },
    },
  },
  edges: {
    e01: edge(0.05, 0.02, 0.04, 0.86, 0.009),
    e02: edge(0.04, -0.02, 0.03, 0.72, 0.007),
    e03: edge(0.04, 0.02, 0.03, 0.72, 0.007),
    e04: edge(0.055, -0.025, 0.04, 0.62),
    e05: edge(0.052, 0.03, 0.04, 0.62),
    e06: edge(0.06, -0.03, 0.05, 0.54),
    e07: edge(0.058, 0.028, 0.05, 0.54),
    e08: edge(0.07, -0.038, 0.032, 0.66, 0.007),
    e09: edge(0.072, -0.04, 0.07, 0.58),
    e10: edge(0.068, 0.032, 0.045, 0.72),
    e11: edge(0.062, -0.026, 0.045, 0.76),
    e12: edge(0.056, 0.02, 0.038, 0.82, 0.007),
    e13: edge(0.062, 0.032, 0.05, 0.64),
    e14: edge(0.068, 0.026, 0.046, 0.68),
    e15: edge(0.067, -0.026, 0.036, 0.72),
    e16: edge(0.074, -0.032, 0.055, 0.66),
    e17: edge(0.052, 0.022, 0.03, 0.76),
    e18: edge(0.05, -0.02, 0.03, 0.74),
    e19: edge(0.08, 0.045, 0.09, 0.38),
    e20: edge(0.072, -0.04, 0.065, 0.4),
    e21: edge(0.064, 0.035, 0.052, 0.58),
  },
  orbitals: {
    "goals-do": orbit(1.62, 0.26, 1.06, 0.18, 0.4, "compute", 0.08, 0.9, 0.22),
    "goals-d1": orbit(2.18, -0.18, 1.02, -0.14, 2.2, "infra", 0.072, 0.78, 0.18),
    "goals-futures-ui": orbit(2.74, 0.13, 1.11, 0.11, 3.6, "signal", 0.07, 0.72, 0.16),
    "goals-ledger": orbit(3.3, -0.1, 0.94, -0.06, 5.5, "core", 0.066, 0.66, 0.14),
    "kaim-worker": orbit(6.1, 0.12, BH_PLANE_X, BH_PLANE_Z, 1.1, "core", 0.07, 0.82, 0.11),
    "kaim-studio-api": orbit(8.1, -0.095, BH_PLANE_X, BH_PLANE_Z, 3.4, "signal", 0.064, 0.68, 0.095),
    "kaim-access": orbit(10.0, 0.074, BH_PLANE_X, BH_PLANE_Z, 5.2, "infra", 0.06, 0.62, 0.082),
    "kaim-design-system": orbit(11.8, -0.058, BH_PLANE_X, BH_PLANE_Z, 4.05, "memory", 0.058, 0.58, 0.07),
    "kaim-nexus-connector": orbit(13.6, 0.051, BH_PLANE_X, BH_PLANE_Z, 2.45, "signal", 0.06, 0.66, 0.065),
    "build-brief": orbit(1.66, 0.24, 0.98, 0.14, 0.7, "core", 0.08, 0.9, 0.22),
    "build-documents": orbit(2.22, -0.17, 1.08, -0.12, 2.5, "memory", 0.072, 0.74, 0.18),
    "build-plot-intel": orbit(2.8, 0.11, 0.92, 0.09, 4.7, "infra", 0.068, 0.68, 0.15),
    "build-timeline": orbit(3.36, -0.08, 1.16, -0.04, 5.8, "signal", 0.064, 0.62, 0.12),
    "photos-archive-index": orbit(1.55, 0.19, 1.02, 0.12, 0.8, "memory", 0.072, 0.72, 0.19),
    "photos-object-store": orbit(2.15, -0.13, 0.96, -0.09, 2.7, "infra", 0.066, 0.62, 0.14),
    "photos-roza-instance": orbit(2.72, 0.1, 1.12, 0.08, 4.9, "signal", 0.07, 0.74, 0.13),
    "home-assistant": orbit(1.58, 0.18, 1.04, 0.11, 0.4, "compute", 0.074, 0.78, 0.18),
    "home-physical-host": orbit(2.22, -0.12, 0.94, -0.1, 2.5, "infra", 0.068, 0.66, 0.14),
    "home-remote-access": orbit(2.82, 0.09, 1.13, 0.06, 4.7, "signal", 0.066, 0.64, 0.12),
    "okolica-cadastre-planning": orbit(1.5, 0.2, 0.96, 0.13, 0.5, "infra", 0.07, 0.72, 0.18),
    "okolica-terrain-environment": orbit(2.08, -0.14, 1.08, -0.09, 2.1, "memory", 0.066, 0.64, 0.14),
    "okolica-mobility": orbit(2.65, 0.1, 0.92, 0.08, 3.9, "signal", 0.064, 0.62, 0.12),
    "okolica-evidence-export": orbit(3.18, -0.075, 1.13, -0.04, 5.5, "core", 0.062, 0.58, 0.11),
    "breakglass-edge-runtime": orbit(1.62, 0.18, 1.0, 0.1, 1.0, "compute", 0.07, 0.72, 0.17),
    "breakglass-scenario-engine": orbit(2.28, -0.12, 1.09, -0.08, 3.8, "signal", 0.066, 0.66, 0.13),
  },
};
